import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Mail, Shield, CheckCircle, AlertCircle } from 'lucide-react'

const VerificationRequiredPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const message = location.state?.message || 'Your account must be verified before accessing this feature.'

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-blue-600" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            Verification Required
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Complete your account verification to continue
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            {/* Alert Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Account Verification Required
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>{message}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Steps */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Next Steps:</h3>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Check Your Email</p>
                    <p className="text-sm text-gray-600">
                      Look for an email from support@evoltbay.com with the subject "Congratulations! Your Enterprise Account Has Been Approved"
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Click Verification Link</p>
                    <p className="text-sm text-gray-600">
                      Click the "Verify Email Address" button in the email to complete your account activation
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Shield className="h-5 w-5 text-purple-600 mt-0.5" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Access Enterprise Features</p>
                    <p className="text-sm text-gray-600">
                      Once verified, you'll have full access to all enterprise features
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => navigate('/auth/login')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to Login
              </button>
              
              <button
                onClick={() => navigate('/')}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Go to Homepage
              </button>
            </div>

            {/* Help Section */}
            <div className="mt-6 border-t border-gray-200 pt-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Didn't receive the email? Check your spam folder or contact support.
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Need help? Contact us at{' '}
                  <a href="mailto:support@evoltbay.com" className="text-blue-600 hover:text-blue-500">
                    support@evoltbay.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerificationRequiredPage 